import React, { useMemo, useState } from 'react';
import { Box, InputBase, Modal, Typography, useTheme } from '@mui/material';

import { AdvancedFilter } from './FilterBar.props';
import { FilterFieldDef, FILTER_FIELD_DEFINITIONS, FILTER_CATEGORIES } from './AddFilterModal.types';
import { AddFilterCategories } from './AddFilterCategories';
import { AddFilterFieldList } from './AddFilterFieldList';
import { AddFilterConfigure } from './AddFilterConfigure';

export interface AddFilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filter: AdvancedFilter) => void;
  recentlyUsed: string[];
}

export const AddFilterModal: React.FC<AddFilterModalProps> = ({
  open,
  onClose,
  onApply,
  recentlyUsed,
}) => {
  const theme = useTheme();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedField, setSelectedField] = useState<FilterFieldDef | null>(null);

  const categories = useMemo(() => {
    const counts: Record<string, number> = {};
    FILTER_FIELD_DEFINITIONS.forEach((f) => {
      counts[f.category] = (counts[f.category] || 0) + 1;
    });
    return FILTER_CATEGORIES.map((name) => ({
      name,
      count: name === 'All' ? FILTER_FIELD_DEFINITIONS.length : (counts[name] || 0),
    }));
  }, []);

  const handleFieldSelect = (field: FilterFieldDef) => {
    setSelectedField(field);
  };

  const handleBack = () => {
    setSelectedField(null);
  };

  const handleApply = (filter: AdvancedFilter) => {
    onApply(filter);
    setSelectedField(null);
    setSearchQuery('');
    onClose();
  };

  const handleClose = () => {
    setSelectedField(null);
    setSearchQuery('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="add-filter-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 780,
          height: 620,
          bgcolor: theme.palette.v4.surface,
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: `1px solid ${theme.palette.v4.border}` }}>
          <Typography id="add-filter-modal-title" variant="subtitle1" sx={{ fontWeight: 600 }}>
            Add Filter
          </Typography>
          {!selectedField && (
            <InputBase
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              inputProps={{ 'aria-label': 'Search filter fields' }}
              sx={{
                mt: 1,
                border: `1px solid ${theme.palette.v4.filterInputBorder}`,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                fontSize: '0.875rem',
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {selectedField ? (
            <AddFilterConfigure
              field={selectedField}
              onApply={handleApply}
              onBack={handleBack}
            />
          ) : (
            <>
              <AddFilterCategories
                categories={categories}
                active={activeCategory}
                onSelect={setActiveCategory}
              />
              <AddFilterFieldList
                fields={FILTER_FIELD_DEFINITIONS}
                category={activeCategory}
                recentlyUsed={recentlyUsed}
                searchQuery={searchQuery}
                onSelect={handleFieldSelect}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
